import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { toast } from 'react-toastify';
import ImageLibraryDialog from './ImageLibraryDialog';
import { useMailEditorConfig } from './mailEditorConfig';

// Code editor for an HTML building block: a large monospace textarea with a
// toolbar to insert personalization variables at the caret and to load an .html
// file. The textarea is uncontrolled per repo convention; the value is handed to
// the parent only when the dialog is confirmed.
const HtmlCodeDialog = ({ open, value, onClose, onConfirm, disabled }) => {
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const selectionRef = useRef({ start: 0, end: 0 });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const { variables, variableHint, listAssets } = useMailEditorConfig();

  useEffect(() => {
    if (!open) setMenuAnchor(null);
  }, [open]);

  const rememberSelection = () => {
    const textarea = textareaRef.current;
    if (textarea) selectionRef.current = { start: textarea.selectionStart, end: textarea.selectionEnd };
  };

  const insertAtCaret = snippet => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const current = textarea.value;
    const { start, end } = selectionRef.current;
    const from = Number.isInteger(start) ? start : current.length;
    const to = Number.isInteger(end) ? end : current.length;
    textarea.value = current.slice(0, from) + snippet + current.slice(to);
    const caret = from + snippet.length;
    textarea.focus();
    textarea.setSelectionRange(caret, caret);
    selectionRef.current = { start: caret, end: caret };
  };

  const insertVariable = token => {
    setMenuAnchor(null);
    insertAtCaret(token);
  };

  // Inserts a ready-to-use img tag for a library image at the caret, so a hand
  // written HTML block can use the same images as the image element.
  const insertImage = asset => {
    if (!asset?.url) return;
    const alt = String(asset.fileName || '').replace(/"/g, '&quot;');
    insertAtCaret(
      `<img src="${asset.url}" alt="${alt}" style="display:block;max-width:100%;height:auto;border:0;" />`
    );
  };

  const handleFileChange = event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const isHtml = file.type === 'text/html' || /\.html?$/i.test(file.name);
    if (!isHtml) {
      toast.error('Bitte eine HTML-Datei (.html) auswählen.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      if (textareaRef.current) textareaRef.current.value = text;
      selectionRef.current = { start: text.length, end: text.length };
    };
    reader.onerror = () => toast.error('Fehler beim Lesen der HTML-Datei');
    reader.readAsText(file);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='lg'>
      <DialogTitle>HTML bearbeiten</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {variables.length > 0 && (
            <>
              <Button
                variant='outlined'
                size='small'
                startIcon={<CodeIcon />}
                disabled={disabled}
                onClick={event => {
                  rememberSelection();
                  setMenuAnchor(event.currentTarget);
                }}
              >
                Variable einfügen
              </Button>
              <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                {variables.map(variable => (
                  <MenuItem key={variable.token} onClick={() => insertVariable(variable.token)}>
                    {variable.label ? `${variable.label} (${variable.token})` : variable.token}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          {listAssets && (
            <Button
              variant='outlined'
              size='small'
              startIcon={<PhotoLibraryIcon />}
              disabled={disabled}
              onClick={() => {
                rememberSelection();
                setLibraryOpen(true);
              }}
            >
              Bild einfügen
            </Button>
          )}
          <Button
            variant='outlined'
            size='small'
            startIcon={<UploadFileIcon />}
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
          >
            HTML-Datei hochladen
          </Button>
          <input ref={fileInputRef} type='file' accept='.html,text/html' hidden onChange={handleFileChange} />
        </Box>
        <ImageLibraryDialog
          open={libraryOpen}
          title='Bild aus der Ablage einfügen'
          onClose={() => setLibraryOpen(false)}
          onSelect={insertImage}
        />
        {variableHint && (
          <Alert severity='info' sx={{ py: 0.25 }}>
            {variableHint}
          </Alert>
        )}
        <Box
          component='textarea'
          ref={textareaRef}
          key={open ? 'open' : 'closed'}
          defaultValue={value || ''}
          disabled={disabled}
          spellCheck={false}
          wrap='off'
          aria-label='HTML-Inhalt'
          placeholder='<table> … </table>'
          onSelect={rememberSelection}
          onKeyUp={rememberSelection}
          onClick={rememberSelection}
          onBlur={rememberSelection}
          sx={{
            minHeight: '55vh',
            width: '100%',
            boxSizing: 'border-box',
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            resize: 'vertical',
            fontFamily: 'monospace',
            fontSize: '14px',
            lineHeight: 1.5,
            whiteSpace: 'pre',
            overflow: 'auto',
            backgroundColor: 'background.paper',
            color: 'text.primary',
            '&:focus': { outline: 'none', borderColor: 'primary.main' },
            '&:disabled': { backgroundColor: 'action.disabledBackground' },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant='contained'
          disabled={disabled}
          onClick={() => {
            onConfirm(textareaRef.current?.value ?? '');
            onClose();
          }}
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HtmlCodeDialog;
